import {
    AutomaticContainer,
    AutomaticContainerSource,
    CartProductItem,
} from '@/features/pos/components/Cart/parts/Overview/parts/CartItem/types'
import { AutomaticContainerRemoval } from '@/features/pos/types'

const getAutomaticContainerSourceKey = (source: AutomaticContainerRemoval): string =>
    source.cartItemUuid !== null && source.cartItemUuid !== undefined
        ? `cart-item:${source.cartItemUuid}`
        : `added-product-variant:${source.cartItemAddedProductVariantUuid}`

export const getAutomaticContainerId = (source: AutomaticContainerSource): string =>
    getAutomaticContainerSourceKey(source)

export class AutomaticContainerRemovals {
    private constructor(
        private readonly removalsBySource: ReadonlyMap<string, AutomaticContainerRemoval>
    ) {}

    static from(removals: readonly AutomaticContainerRemoval[]): AutomaticContainerRemovals {
        return new AutomaticContainerRemovals(
            new Map(removals.map((removal) => [getAutomaticContainerSourceKey(removal), removal]))
        )
    }

    has(source: AutomaticContainerSource): boolean {
        return this.removalsBySource.has(getAutomaticContainerSourceKey(source))
    }

    replaceFor(
        containersInScope: readonly AutomaticContainer[],
        nextRemovedContainers: readonly AutomaticContainer[]
    ): AutomaticContainerRemovals {
        const nextRemovalsBySource = new Map(this.removalsBySource)

        containersInScope.forEach(({ source }) =>
            nextRemovalsBySource.delete(getAutomaticContainerSourceKey(source))
        )
        nextRemovedContainers.forEach(({ source }) =>
            nextRemovalsBySource.set(getAutomaticContainerSourceKey(source), source)
        )

        return new AutomaticContainerRemovals(nextRemovalsBySource)
    }

    reconcileWith(items: readonly CartProductItem[]): AutomaticContainerRemovals {
        const currentSources = new Map<string, AutomaticContainerSource>()

        items.forEach((item) => {
            item.containers?.forEach(({ source }) => {
                currentSources.set(getAutomaticContainerSourceKey(source), source)
            })
        })

        return AutomaticContainerRemovals.from(
            this.toArray().flatMap((removal) => {
                const source = currentSources.get(getAutomaticContainerSourceKey(removal))

                return source ? [source] : []
            })
        )
    }

    applyTo(items: CartProductItem[]): CartProductItem[] {
        if (!this.removalsBySource.size) return items

        return items.map((item) => ({
            ...item,
            containers: item.containers?.filter(({ source }) => !this.has(source)),
        }))
    }

    toArray(): AutomaticContainerRemoval[] {
        return Array.from(this.removalsBySource.values())
    }
}
